import { useMemo, useState, useEffect, useCallback } from 'react';
import { View, Pressable, ActivityIndicator, TextInput, Modal, KeyboardAvoidingView, Platform, ScrollView } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SelectField } from '@/components/ui/SelectField';
import { InputField } from '@/components/ui/InputField';
import { Body, Caption, ThemedText, SubHeading } from '@/components/ui/Text';
import { BudgetSummary } from '@/components/expenses/BudgetSummary';
import { ExpenseSummaryWidget } from '@/components/expenses/ExpenseSummaryWidget';
import { TripQuickStats } from '@/components/expenses/TripQuickStats';
import { CollapsibleSection } from '@/components/ui/CollapsibleSection';
import { ExpenseList } from '@/components/expenses/ExpenseList';
import { ExpenseDetailModal } from '@/components/expenses/ExpenseDetailModal';
import { ReceiptUpload } from '@/components/expenses/ReceiptUpload';
import { ExpenseSettlementView } from '@/components/expenses/ExpenseSettlementView';
import { ExpenseSplitSelector } from '@/components/expenses/ExpenseSplitSelector';
import { TripComments } from '@/components/trips/TripComments';
import { formatCurrency, getExchangeRate, getExchangeRateCacheInfo } from '@/utils/currency';
import type { Trip } from '@/services/trips';
import { updateBooking, type Booking, type BookingSplit } from '@/services/bookings';
import type { Expense, ExpenseSplit } from '@/services/expenses';
import type { TripBudgetSnapshot, BudgetCategoryAllocations } from '@/types/budget';
import type { ItineraryItem } from '@/services/itinerary';
import { canUserEditItem } from '@/services/collaboration';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { CURRENCY_OPTIONS } from '@/data/profileOptions';
import type { InboxProviderId } from '@/services/inboxSync';
import { getTripParticipants } from '@/utils/tripParticipants';
import { TripCostConfidenceCard } from '@/components/planning/TripCostConfidenceCard';
import { shouldShowTripCostConfidence } from '@/services/tripCostConfidence';
import type { UserProfile } from '@/state/userStore';


type FinancialTabProps = {
  trip: Trip;
  bookings: Booking[];
  expenses: Expense[];
  isScreenFocused?: boolean;
  isArchived: boolean;
  canEdit: boolean;
  userId: string;
  userPermission?: 'owner' | 'view' | 'edit' | 'add_items' | null;
  isOwner?: boolean;
  onEditExpense: (expense: Expense) => void;
  onDeleteExpense: (expense: Expense) => void;
  onAddExpense: () => void;
  onSyncInbox: () => void;
  inboxSyncing: boolean;
  inboxConnected: boolean | null;
  inboxProviderLabel?: string;
  onChangeInboxProvider?: (provider?: InboxProviderId) => void;
  onReceiptProcessed: (expense: Expense) => void;
  onOpenBudgetEditor?: () => void;
  onSaveCategoryAllocations?: (allocations: BudgetCategoryAllocations) => Promise<void>;
  budgetSnapshot?: TripBudgetSnapshot; // Optional: pre-calculated budget snapshot
  itineraryItems?: ItineraryItem[]; // For export functionality
  onOpenExportMenu?: () => void; // For export functionality
  profile?: Pick<UserProfile, 'travelType'>;
  costConfidenceDismissed?: boolean;
  onDismissCostConfidence?: () => void;
  onApplyBudgetFromEstimate?: (amount: number) => void | Promise<void>;
};

export function FinancialTab({
  trip,
  bookings,
  expenses,
  isScreenFocused = true,
  isArchived,
  canEdit,
  userId,
  userPermission,
  isOwner = false,
  onEditExpense,
  onDeleteExpense,
  onAddExpense,
  onSyncInbox,
  inboxSyncing,
  inboxConnected,
  inboxProviderLabel = 'Gmail',
  onChangeInboxProvider,
  onReceiptProcessed,
  onOpenBudgetEditor,
  onSaveCategoryAllocations,
  budgetSnapshot,
  itineraryItems = [],
  onOpenExportMenu,
  profile,
  costConfidenceDismissed = false,
  onDismissCostConfidence,
  onApplyBudgetFromEstimate,
}: FinancialTabProps) {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const showCostEstimateCard =
    shouldShowTripCostConfidence(trip, isArchived) && !costConfidenceDismissed;
  const [bookingsSectionExpanded, setBookingsSectionExpanded] = useState(false);
  const [expensesSectionExpanded, setExpensesSectionExpanded] = useState(false);
  const [settlementSectionExpanded, setSettlementSectionExpanded] = useState(true);
  const [viewingExpense, setViewingExpense] = useState<Expense | null>(null);
  const [expenseSearchQuery, setExpenseSearchQuery] = useState('');
  const [bookingSearchQuery, setBookingSearchQuery] = useState('');
  // Edit booking state
  const [editingBooking, setEditingBooking] = useState<Booking | null>(null);
  const [editAmount, setEditAmount] = useState('');
  const [editCurrency, setEditCurrency] = useState('');
  const [editSplit, setEditSplit] = useState<ExpenseSplit | null>(null);
  /** Seed for ExpenseSplitSelector — set once when modal opens, not live editSplit (avoids update loops). */
  const [editSplitSeed, setEditSplitSeed] = useState<ExpenseSplit | null>(null);
  const [isSavingBooking, setIsSavingBooking] = useState(false);
  // Converted total state
  const [convertedTotal, setConvertedTotal] = useState<number | null>(null);
  const [isConvertingTotal, setIsConvertingTotal] = useState(false);
  const [conversionRates, setConversionRates] = useState<Record<string, number> | null>(null);
  const [rateCacheInfo, setRateCacheInfo] = useState<ReturnType<typeof getExchangeRateCacheInfo> | null>(null);
  const tripParticipants = useMemo(() => getTripParticipants(trip, userId), [trip, userId]);
  const canEditSingleExpense = useCallback(
    (expense: Expense) => {
      if (!userPermission) return false;
      return canUserEditItem(userId, expense.createdBy, userPermission, isOwner);
    },
    [userPermission, userId, isOwner],
  );

  // Filter expenses based on search query
  const filteredExpenses = useMemo(() => {
    if (!expenseSearchQuery.trim()) return expenses;
    const query = expenseSearchQuery.toLowerCase();
    return expenses.filter(expense =>
      expense.description?.toLowerCase().includes(query) ||
      expense.category?.toLowerCase().includes(query) ||
      expense.amount.toString().includes(query) ||
      expense.currency?.toLowerCase().includes(query)
    );
  }, [expenses, expenseSearchQuery]);

  // Filter bookings based on search query
  const filteredBookings = useMemo(() => {
    if (!bookingSearchQuery.trim()) return bookings;
    const query = bookingSearchQuery.toLowerCase();
    return bookings.filter(booking =>
      booking.provider?.toLowerCase().includes(query) ||
      booking.location?.toLowerCase().includes(query) ||
      booking.type?.toLowerCase().includes(query) ||
      booking.confirmationNumber?.toLowerCase().includes(query) ||
      (booking.amount && booking.amount.toString().includes(query))
    );
  }, [bookings, bookingSearchQuery]);

  // Calculate booking stats with currency breakdown
  const bookingStats = useMemo(() => {
    let countWithAmount = 0;
    let countWithoutAmount = 0;
    const byCurrency: Record<string, number> = {};
    
    bookings.forEach(booking => {
      const amount =
        typeof booking.amount === 'number' ? booking.amount : Number((booking as any).amount);
      if (Number.isFinite(amount) && amount !== 0) {
        const currency = (booking.currency || trip.baseCurrency || 'USD').toUpperCase();
        byCurrency[currency] = (byCurrency[currency] || 0) + amount;
        countWithAmount++;
      } else {
        countWithoutAmount++;
      }
    });
    
    const currencies = Object.keys(byCurrency);
    const hasMultipleCurrencies = currencies.length > 1;
    
    return { 
      byCurrency, 
      countWithAmount, 
      countWithoutAmount, 
      currencies,
      hasMultipleCurrencies,
    };
  }, [bookings, trip.baseCurrency]);

  // Calculate converted total when there are multiple currencies
  useEffect(() => {
    if (!isScreenFocused) return;
    let cancelled = false;
    const timeoutId = setTimeout(() => {
      const calculateConvertedTotal = async () => {
        if (!bookingStats.hasMultipleCurrencies) {
          // Single currency - just use the sum
          const firstCurrency = bookingStats.currencies[0];
          if (cancelled) return;
          if (firstCurrency) {
            setConvertedTotal(bookingStats.byCurrency[firstCurrency] || 0);
          } else {
            setConvertedTotal(0);
          }
          setConversionRates(null);
          setRateCacheInfo(null);
          return;
        }

        setIsConvertingTotal(true);
        try {
          const base = (trip.baseCurrency || 'USD').toUpperCase();
          const currencies = Object.keys(bookingStats.byCurrency);

          // Build a rate map so we can show per-currency "≈" contributions in the UI.
          const rateEntries = await Promise.all(
            currencies.map(async (cur) => {
              const upper = cur.toUpperCase();
              if (upper === base) return [upper, 1.0] as const;
              const rate = await getExchangeRate(upper as any, base as any);
              return [upper, rate] as const;
            }),
          );
          if (cancelled) return;

          const ratesObj: Record<string, number> = Object.fromEntries(rateEntries);
          let total = 0;
          for (const [cur, amount] of Object.entries(bookingStats.byCurrency)) {
            const upper = cur.toUpperCase();
            total += (Number(amount) || 0) * (ratesObj[upper] ?? 1.0);
          }

          if (cancelled) return;
          setConversionRates(ratesObj);
          setRateCacheInfo(getExchangeRateCacheInfo());
          setConvertedTotal(total);
        } catch (error) {
          console.error('[FinancialTab] Failed to convert booking totals:', error);
          if (cancelled) return;
          // Never "sum across currencies" as a fallback — that produces incorrect totals.
          // Instead, show 0 and let the breakdown still be correct.
          setConversionRates(null);
          setRateCacheInfo(getExchangeRateCacheInfo());
          setConvertedTotal(null);
        } finally {
          if (!cancelled) {
            setIsConvertingTotal(false);
          }
        }
      };
      void calculateConvertedTotal();
    }, 150);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [bookingStats.byCurrency, bookingStats.hasMultipleCurrencies, bookingStats.currencies, trip.baseCurrency, isScreenFocused]);

  // Handle saving booking edit
  const handleSaveBookingEdit = async () => {
    if (!editingBooking) return;
    
    setIsSavingBooking(true);
    try {
      const amount = parseFloat(editAmount);
      if (isNaN(amount) || amount < 0) {
        throw new Error('Invalid amount');
      }
      
      // Convert ExpenseSplit to BookingSplit if present
      const splitUpdate: BookingSplit | null = editSplit ? {
        splitType: editSplit.splitType,
        splitAmong: editSplit.splitAmong,
        paidBy: editSplit.paidBy,
        splitAmounts: editSplit.splitAmounts,
      } : null;
      
      await updateBooking(trip.id, editingBooking.id, {
        amount,
        currency: editCurrency || trip.baseCurrency || 'USD',
        split: splitUpdate,
      });
      
      setEditingBooking(null);
      setEditAmount('');
      setEditCurrency('');
      setEditSplit(null);
      setEditSplitSeed(null);
    } catch (error) {
      console.error('[FinancialTab] Failed to update booking:', error);
      // TODO: Show error toast
    } finally {
      setIsSavingBooking(false);
    }
  };

  // Open edit modal for a booking
  const handleEditBooking = (booking: Booking) => {
    setEditingBooking(booking);
    setEditAmount(booking.amount?.toString() || '');
    setEditCurrency(booking.currency || trip.baseCurrency || 'USD');
    // Initialize split state from booking if it has one
    const seed = booking.split
      ? {
          splitType: booking.split.splitType,
          splitAmong: booking.split.splitAmong,
          paidBy: booking.split.paidBy,
          splitAmounts: booking.split.splitAmounts,
        }
      : null;
    setEditSplitSeed(seed);
    setEditSplit(seed);
  };

  // Get booking type icon and label
  const getBookingTypeInfo = (type: string) => {
    const typeMap: Record<string, { icon: string; label: string; color: string }> = {
      flight: { icon: 'airplane', label: 'Flight', color: theme.colors.primary },
      hotel: { icon: 'bed', label: 'Hotel', color: '#10B981' },
      car: { icon: 'car', label: 'Car Rental', color: '#F59E0B' },
      restaurant: { icon: 'restaurant', label: 'Restaurant', color: '#EC4899' },
      activity: { icon: 'ticket', label: 'Activity', color: '#8B5CF6' },
      other: { icon: 'receipt', label: 'Booking', color: theme.colors.textSecondary },
    };
    return typeMap[type] || typeMap.other;
  };

  return (
    <View style={{ 
      paddingBottom: theme.spacing(4),
    }}>
      <TripCostConfidenceCard
        trip={trip}
        bookings={bookings}
        profile={profile}
        isArchived={isArchived}
        dismissed={costConfidenceDismissed}
        onDismiss={onDismissCostConfidence}
        onApplyBudget={onApplyBudgetFromEstimate}
        onOpenCustomBudget={onOpenBudgetEditor}
      />

      {/* Prominent Set Budget Callout - when no estimate card is shown */}
      {!trip.budget && !showCostEstimateCard && canEdit && !isArchived && onOpenBudgetEditor && (
        <Card 
          variant="elevated"
          style={{
            marginBottom: theme.spacing(2),
            backgroundColor: theme.colors.primary + '10',
            borderWidth: 2,
            borderColor: theme.colors.primary + '30',
          }}
        >
          <View style={{ 
            flexDirection: 'row', 
            alignItems: 'center', 
            gap: theme.spacing(2),
          }}>
            <View style={{
              width: 48,
              height: 48,
              borderRadius: theme.radii.lg,
              backgroundColor: theme.colors.primary + '20',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Ionicons name="wallet" size={24} color={theme.colors.primary} />
            </View>
            <View style={{ flex: 1, gap: theme.spacing(0.5) }}>
              <ThemedText variant="bodyStrong" style={{ fontSize: 16 }}>
                Set Your Budget
              </ThemedText>
              <Body color={theme.colors.textSecondary} style={{ fontSize: 13 }}>
                Track your spending and stay on budget for this trip
              </Body>
              <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
                Estimates update as you add bookings.
              </Caption>
            </View>
            <Pressable
              onPress={onOpenBudgetEditor}
              style={({ pressed }) => ({
                paddingHorizontal: theme.spacing(2),
                paddingVertical: theme.spacing(1.25),
                borderRadius: theme.radii.md,
                backgroundColor: theme.colors.primary,
                opacity: pressed ? 0.8 : 1,
              })}
            >
              <ThemedText variant="bodyStrong" style={{ color: theme.colorScheme === 'dark' ? '#000000' : '#FFFFFF', fontSize: 14, fontWeight: '600' }}>
                Set Budget
              </ThemedText>
            </Pressable>
          </View>
        </Card>
      )}

      {/* Budget Summary */}
      <BudgetSummary 
        trip={trip}
        bookings={bookings} 
        expenses={expenses}
        budgetSnapshot={budgetSnapshot}
        onOpenBudgetEditor={onOpenBudgetEditor}
        canEdit={canEdit && !isArchived}
        onSaveCategoryAllocations={onSaveCategoryAllocations}
      />
      
      {/* Receipt Upload - moved to top for better visibility */}
      {!isArchived && canEdit && (
        <ReceiptUpload
          tripId={trip.id}
          userId={userId}
          defaultCurrency={trip.baseCurrency || (trip.locale === 'en-US' ? 'USD' : 'EUR')}
          onReceiptProcessed={onReceiptProcessed}
        />
      )}

      {/* Expense Summary Widget */}
      <ExpenseSummaryWidget
        trip={trip}
        expenses={expenses}
        onViewAll={() => {
          setExpensesSectionExpanded(true);
        }}
        onAddExpense={onAddExpense}
      />

      {/* Synced Bookings - Shows bookings from email sync */}
      <CollapsibleSection
        title={`Synced Bookings (${bookings.length})`}
        icon="mail-outline"
        expanded={bookingsSectionExpanded}
        onExpandedChange={setBookingsSectionExpanded}
        defaultExpanded={false}
      >
        {bookings.length > 0 && (
          <View style={{ marginBottom: theme.spacing(1.5) }}>
            {/* Provider management */}
            {!isArchived && (
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  paddingVertical: theme.spacing(1),
                  paddingHorizontal: theme.spacing(1),
                  backgroundColor: theme.colors.surface,
                  borderRadius: theme.radii.md,
                  marginBottom: theme.spacing(1),
                  borderWidth: 1,
                  borderColor: theme.colors.borderMuted,
                  gap: theme.spacing(1),
                }}
              >
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: theme.spacing(1),
                    flexGrow: 1,
                    flexShrink: 1,
                    flexBasis: 0,
                    minWidth: 0,
                  }}
                >
                  <Ionicons
                    name={inboxConnected ? 'mail-open-outline' : 'mail-outline'}
                    size={18}
                    color={inboxConnected ? theme.colors.primary : theme.colors.textSecondary}
                  />
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
                      Email sync
                    </Caption>
                    <Caption
                      style={{ fontWeight: '700' }}
                      numberOfLines={1}
                      ellipsizeMode="tail"
                    >
                      {inboxConnected === null
                        ? 'Checking connection…'
                        : inboxConnected
                          ? `Connected • ${inboxProviderLabel}`
                          : 'Not connected'}
                    </Caption>
                  </View>
                </View>

                <Button
                  label="Connect another email"
                  variant="ghost"
                  onPress={() => onChangeInboxProvider?.()}
                  size="small"
                  labelNumberOfLines={2}
                  style={{ flexShrink: 1, minWidth: 0, alignSelf: 'center' }}
                />
              </View>
            )}

            {/* Stats summary with currency breakdown */}
            <View style={{
              paddingVertical: theme.spacing(1),
              paddingHorizontal: theme.spacing(1),
              backgroundColor: theme.colors.surface,
              borderRadius: theme.radii.md,
              marginBottom: theme.spacing(1),
              borderWidth: 1,
              borderColor: theme.colors.borderMuted,
              gap: theme.spacing(0.75),
            }}>
              {/* Header row */}
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                  <Ionicons name="checkmark-circle" size={16} color={theme.colors.success} />
                  <Caption color={theme.colors.textSecondary}>
                    {bookingStats.countWithAmount} with price data
                  </Caption>
                </View>
                {isConvertingTotal ? (
                  <ActivityIndicator size="small" color={theme.colors.primary} />
                ) : (
                  <ThemedText variant="bodyStrong" style={{ fontSize: 14, color: theme.colors.success }}>
                    ≈ {formatCurrency(convertedTotal || 0, trip.baseCurrency || 'USD')}
                  </ThemedText>
                )}
              </View>
              
              {/* Currency breakdown */}
              {bookingStats.hasMultipleCurrencies && (
                <View style={{ 
                  paddingTop: theme.spacing(0.5),
                  borderTopWidth: 1,
                  borderTopColor: theme.colors.borderMuted,
                  gap: theme.spacing(0.25),
                }}>
                  <Caption color={theme.colors.textSecondary} style={{ fontSize: 10, marginBottom: 2 }}>
                    Currency breakdown:
                  </Caption>
                  {Object.entries(bookingStats.byCurrency).map(([currency, amount]) => (
                    <View key={currency} style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                      <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
                        {currency}
                      </Caption>
                      <View style={{ alignItems: 'flex-end' }}>
                      <Caption color={theme.colors.text} style={{ fontSize: 11, fontWeight: '600' }}>
                        {formatCurrency(amount, currency)}
                      </Caption>
                        {(() => {
                          const base = (trip.baseCurrency || 'USD').toUpperCase();
                          const cur = currency.toUpperCase();
                          const rate = conversionRates?.[cur];
                          if (!rate || cur === base) return null;
                          const converted = (Number(amount) || 0) * rate;
                          return (
                            <Caption color={theme.colors.textSecondary} style={{ fontSize: 10 }}>
                              ≈ {formatCurrency(converted, base)}
                            </Caption>
                          );
                        })()}
                      </View>
                    </View>
                  ))}
                  {rateCacheInfo?.lastUpdated && (
                    <Caption color={theme.colors.textSecondary} style={{ fontSize: 10, marginTop: theme.spacing(0.5) }}>
                      Rates cached • updated {rateCacheInfo.lastUpdated.toLocaleDateString()}
                    </Caption>
                  )}
                </View>
              )}
            </View>
            
            {/* Search */}
            <View style={{ 
              flexDirection: 'row', 
              alignItems: 'center', 
              backgroundColor: theme.colors.surface,
              borderRadius: theme.radii.md,
              paddingHorizontal: theme.spacing(1.5),
              paddingVertical: theme.spacing(1),
              borderWidth: 1,
              borderColor: theme.colors.borderMuted,
              gap: theme.spacing(1),
            }}>
              <Ionicons name="search-outline" size={20} color={theme.colors.textSecondary} />
              <TextInput
                value={bookingSearchQuery}
                onChangeText={setBookingSearchQuery}
                placeholder="Search bookings..."
                placeholderTextColor={theme.colors.textSecondary}
                style={{
                  flex: 1,
                  fontSize: 15,
                  color: theme.colors.text,
                  paddingVertical: theme.spacing(0.5),
                  minHeight: 44,
                }}
              />
              {bookingSearchQuery.length > 0 && (
                <Pressable
                  onPress={() => setBookingSearchQuery('')}
                  style={{ padding: theme.spacing(0.5), minWidth: 44, minHeight: 44, justifyContent: 'center', alignItems: 'center' }}
                >
                  <Ionicons name="close-circle" size={20} color={theme.colors.textSecondary} />
                </Pressable>
              )}
            </View>
          </View>
        )}
        
        {bookings.length === 0 ? (
          <View style={{ alignItems: 'center', paddingVertical: theme.spacing(3), gap: theme.spacing(1.5) }}>
            <Ionicons name="mail-unread-outline" size={40} color={theme.colors.textSecondary} />
            <Body color={theme.colors.textSecondary} style={{ textAlign: 'center', fontWeight: '600' }}>
              No bookings synced yet
            </Body>
            <Caption color={theme.colors.textSecondary} style={{ textAlign: 'center', paddingHorizontal: theme.spacing(2) }}>
              Connect your email to automatically import flights, hotels, and other travel bookings.
            </Caption>
            
            {!isArchived && (
              <View style={{ gap: theme.spacing(1), marginTop: theme.spacing(1), width: '100%', paddingHorizontal: theme.spacing(2) }}>
                {/* Provider buttons */}
                <View style={{ flexDirection: 'row', gap: theme.spacing(1), justifyContent: 'center' }}>
                  {/* Gmail */}
                  <Pressable
                    onPress={() => onChangeInboxProvider?.('gmail')}
                    style={({ pressed }) => ({
                      flex: 1,
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: theme.spacing(0.75),
                      paddingVertical: theme.spacing(1.25),
                      paddingHorizontal: theme.spacing(1.5),
                      backgroundColor: '#EA4335' + '15',
                      borderRadius: theme.radii.md,
                      borderWidth: 1,
                      borderColor: '#EA4335' + '30',
                      opacity: pressed ? 0.7 : 1,
                    })}
                  >
                    <Ionicons name="mail" size={18} color="#EA4335" />
                    <Caption style={{ color: '#EA4335', fontWeight: '600' }}>Gmail</Caption>
                  </Pressable>

                  {/* Outlook */}
                  <Pressable
                    onPress={() => onChangeInboxProvider?.('outlook')}
                    style={({ pressed }) => ({
                      flex: 1,
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: theme.spacing(0.75),
                      paddingVertical: theme.spacing(1.25),
                      paddingHorizontal: theme.spacing(1.5),
                      backgroundColor: '#0078D4' + '15',
                      borderRadius: theme.radii.md,
                      borderWidth: 1,
                      borderColor: '#0078D4' + '30',
                      opacity: pressed ? 0.7 : 1,
                    })}
                  >
                    <Ionicons name="mail" size={18} color="#0078D4" />
                    <Caption style={{ color: '#0078D4', fontWeight: '600' }}>Outlook</Caption>
                  </Pressable>

                  {/* iCloud */}
                  <Pressable
                    onPress={() => onChangeInboxProvider?.('icloud')}
                    style={({ pressed }) => ({
                      flex: 1,
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: theme.spacing(0.75),
                      paddingVertical: theme.spacing(1.25),
                      paddingHorizontal: theme.spacing(1.5),
                      backgroundColor: '#007AFF' + '15',
                      borderRadius: theme.radii.md,
                      borderWidth: 1,
                      borderColor: '#007AFF' + '30',
                      opacity: pressed ? 0.7 : 1,
                    })}
                  >
                    <Ionicons name="cloud-outline" size={18} color="#007AFF" />
                    <Caption style={{ color: '#007AFF', fontWeight: '600' }}>iCloud</Caption>
                  </Pressable>
                </View>

                {/* Syncing indicator if active */}
                {inboxSyncing && (
                  <View style={{ 
                    flexDirection: 'row', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    gap: theme.spacing(1),
                    paddingVertical: theme.spacing(1),
                  }}>
                    <ActivityIndicator size="small" color={theme.colors.primary} />
                    <Caption color={theme.colors.primary}>Syncing {inboxProviderLabel}...</Caption>
                  </View>
                )}
              </View>
            )}
          </View>
        ) : filteredBookings.length === 0 && bookingSearchQuery.trim() ? (
          <View style={{ alignItems: 'center', paddingVertical: theme.spacing(3), gap: theme.spacing(1) }}>
            <Ionicons name="search-outline" size={32} color={theme.colors.textSecondary} />
            <Body color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
              No bookings match "{bookingSearchQuery}"
            </Body>
          </View>
        ) : (
          <View style={{ gap: theme.spacing(1) }}>
            {filteredBookings.map((booking) => {
              const typeInfo = getBookingTypeInfo(booking.type);
              const hasAmount = booking.amount && !isNaN(booking.amount);
              const bookingDate = booking.departureDate || booking.checkInDate || booking.createdAt;
              
              return (
                <View
                  key={booking.id}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    padding: theme.spacing(1.5),
                    backgroundColor: theme.colors.surface,
                    borderRadius: theme.radii.md,
                    borderWidth: 1,
                    borderColor: hasAmount ? theme.colors.borderMuted : theme.colors.warning + '40',
                    gap: theme.spacing(1.5),
                  }}
                >
                  {/* Type Icon */}
                  <View style={{
                    width: 40,
                    height: 40,
                    borderRadius: theme.radii.md,
                    backgroundColor: typeInfo.color + '15',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <Ionicons name={typeInfo.icon as any} size={20} color={typeInfo.color} />
                  </View>
                  
                  {/* Booking Details */}
                  <View style={{ flex: 1 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
                      <ThemedText variant="bodyStrong" style={{ fontSize: 14 }} numberOfLines={1}>
                        {booking.provider || booking.location || typeInfo.label}
                      </ThemedText>
                      {booking.emailProvider && (
                        <View style={{
                          paddingHorizontal: theme.spacing(0.5),
                          paddingVertical: 2,
                          backgroundColor: theme.colors.borderMuted,
                          borderRadius: 4,
                        }}>
                          <Caption style={{ fontSize: 9, textTransform: 'uppercase' }}>
                            {booking.emailProvider}
                          </Caption>
                        </View>
                      )}
                    </View>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1), marginTop: 2 }}>
                      <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
                        {typeInfo.label}
                      </Caption>
                      {booking.location && booking.location !== booking.provider && (
                        <>
                          <Caption color={theme.colors.textSecondary}>•</Caption>
                          <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }} numberOfLines={1}>
                            {booking.location}
                          </Caption>
                        </>
                      )}
                    </View>
                    {bookingDate && (
                      <Caption color={theme.colors.textSecondary} style={{ fontSize: 10, marginTop: 2 }}>
                        {new Date(bookingDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                      </Caption>
                    )}
                    {booking.confirmationNumber && (
                      <Caption color={theme.colors.textSecondary} style={{ fontSize: 10, marginTop: 1 }}>
                        Conf: {booking.confirmationNumber}
                      </Caption>
                    )}
                  </View>
                  
                  {/* Amount + Edit */}
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                    <View style={{ alignItems: 'flex-end' }}>
                      {hasAmount ? (
                        <ThemedText variant="bodyStrong" style={{ fontSize: 14, color: theme.colors.success }}>
                          {formatCurrency(booking.amount!, booking.currency || trip.baseCurrency || 'USD')}
                        </ThemedText>
                      ) : (
                        <View style={{ alignItems: 'flex-end' }}>
                          <Caption color={theme.colors.warning} style={{ fontSize: 11, fontWeight: '600' }}>
                            No price
                          </Caption>
                          <Caption color={theme.colors.textSecondary} style={{ fontSize: 9 }}>
                            Not in budget
                          </Caption>
                        </View>
                      )}
                    </View>
                    {/* Edit button */}
                    {canEdit && !isArchived && (
                      <Pressable
                        onPress={() => handleEditBooking(booking)}
                        style={({ pressed }) => ({
                          padding: theme.spacing(0.5),
                          opacity: pressed ? 0.5 : 1,
                        })}
                        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                      >
                        <Ionicons name="pencil" size={16} color={theme.colors.textSecondary} />
                      </Pressable>
                    )}
                  </View>
                </View>
              );
            })}
            
            {/* Re-sync button at bottom */}
            {!isArchived && (
              <Pressable
                onPress={onSyncInbox}
                disabled={inboxSyncing}
                style={({ pressed }) => ({
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: theme.spacing(1),
                  paddingVertical: theme.spacing(1.5),
                  marginTop: theme.spacing(0.5),
                  borderRadius: theme.radii.md,
                  borderWidth: 1,
                  borderColor: theme.colors.borderMuted,
                  borderStyle: 'dashed',
                  opacity: pressed ? 0.7 : 1,
                })}
              >
                {inboxSyncing ? (
                  <ActivityIndicator size="small" color={theme.colors.primary} />
                ) : (
                  <Ionicons name="refresh" size={16} color={theme.colors.primary} />
                )}
                <Caption color={theme.colors.primary} style={{ fontWeight: '600' }}>
                  {inboxSyncing ? 'Syncing...' : 'Sync for new bookings'}
                </Caption>
              </Pressable>
            )}
          </View>
        )}
      </CollapsibleSection>

      {/* All Expenses */}
      <CollapsibleSection
        title={`All Expenses (${expenses.length})`}
        icon="list-outline"
        expanded={expensesSectionExpanded}
        onExpandedChange={setExpensesSectionExpanded}
        defaultExpanded={false}
      >
        {expenses.length > 0 && (
          <View style={{ marginBottom: theme.spacing(1.5) }}>
            <View style={{ 
              flexDirection: 'row', 
              alignItems: 'center', 
              backgroundColor: theme.colors.surface,
              borderRadius: theme.radii.md,
              paddingHorizontal: theme.spacing(1.5),
              paddingVertical: theme.spacing(1),
              borderWidth: 1,
              borderColor: theme.colors.borderMuted,
              gap: theme.spacing(1),
            }}>
              <Ionicons name="search-outline" size={20} color={theme.colors.textSecondary} />
              <TextInput
                value={expenseSearchQuery}
                onChangeText={setExpenseSearchQuery}
                placeholder="Search expenses..."
                placeholderTextColor={theme.colors.textSecondary}
                style={{
                  flex: 1,
                  fontSize: 15,
                  color: theme.colors.text,
                  paddingVertical: theme.spacing(0.5),
                  minHeight: 44, // Accessibility: minimum touch target
                }}
                accessibilityLabel="Search expenses"
                accessibilityHint="Type to filter expenses by description, category, or amount"
              />
              {expenseSearchQuery.length > 0 && (
                <Pressable
                  onPress={() => setExpenseSearchQuery('')}
                  style={{ padding: theme.spacing(0.5), minWidth: 44, minHeight: 44, justifyContent: 'center', alignItems: 'center' }}
                  accessibilityRole="button"
                  accessibilityLabel="Clear search"
                >
                  <Ionicons name="close-circle" size={20} color={theme.colors.textSecondary} />
                </Pressable>
              )}
            </View>
          </View>
        )}
        {expenses.length === 0 ? (
          <View style={{ alignItems: 'center', paddingVertical: theme.spacing(3), gap: theme.spacing(1) }}>
            <Ionicons name="receipt-outline" size={32} color={theme.colors.textSecondary} />
            <Body color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
              No expenses yet. Use “Add expense” above to start tracking.
            </Body>
          </View>
        ) : filteredExpenses.length === 0 && expenseSearchQuery.trim() ? (
          <View style={{ alignItems: 'center', paddingVertical: theme.spacing(3), gap: theme.spacing(1) }}>
            <Ionicons name="search-outline" size={32} color={theme.colors.textSecondary} />
            <Body color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
              No expenses match "{expenseSearchQuery}"
            </Body>
          </View>
        ) : (
          <ExpenseList
            expenses={filteredExpenses}
            onEditExpense={onEditExpense}
            onDeleteExpense={onDeleteExpense}
            onViewExpense={setViewingExpense}
            canEdit={canEdit}
            canEditExpense={canEditSingleExpense}
            noCard
            baseCurrency={trip.baseCurrency || 'USD'}
            allowLiveConversion={false}
            tripMembers={tripParticipants}
            currentUserId={userId}
          />
        )}
      </CollapsibleSection>

      {/* Expense Settlements */}
      {tripParticipants.length > 1 && (
        <CollapsibleSection
          title="Expense Settlements"
          icon="calculator-outline"
          expanded={settlementSectionExpanded}
          onExpandedChange={setSettlementSectionExpanded}
          defaultExpanded={true}
        >
          <ExpenseSettlementView
            expenses={expenses}
            trip={trip}
            currentUserId={userId}
          />
        </CollapsibleSection>
      )}

      {/* Trip Comments */}
      <TripComments trip={trip} currentUserId={userId} />

      {/* Expense Detail Modal */}
      <ExpenseDetailModal
        visible={!!viewingExpense}
        expense={viewingExpense}
        onClose={() => setViewingExpense(null)}
        onEdit={(expense) => {
          setViewingExpense(null);
          onEditExpense(expense);
        }}
        onDelete={(expense) => {
          setViewingExpense(null);
          onDeleteExpense(expense);
        }}
        canEdit={canEdit && viewingExpense ? canUserEditItem(userId, viewingExpense.createdBy, userPermission ?? null, isOwner) : false}
        baseCurrency={trip.baseCurrency || 'USD'}
        tripMembers={tripParticipants}
        currentUserId={userId}
      />

      {/* Edit Booking Modal */}
      <Modal
        visible={!!editingBooking}
        animationType="slide"
        transparent
        onRequestClose={() => {
          setEditingBooking(null);
          setEditSplit(null);
          setEditSplitSeed(null);
        }}
      >
        <KeyboardAvoidingView 
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          keyboardVerticalOffset={Platform.OS === 'ios' ? insets.bottom : 0}
          style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' }}
        >
          <Pressable 
            style={{ flex: 0.1 }}
            onPress={() => {
              setEditingBooking(null);
              setEditSplit(null);
              setEditSplitSeed(null);
            }}
          />
          <View
            style={{
              flex: 1,
              backgroundColor: theme.colors.background,
              borderTopLeftRadius: theme.radii.xl,
              borderTopRightRadius: theme.radii.xl,
            }}
          >
              {/* Header */}
              <View style={{ 
                flexDirection: 'row', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                paddingTop: theme.spacing(2),
                paddingHorizontal: theme.spacing(2),
                paddingBottom: theme.spacing(1),
                borderBottomWidth: 1,
                borderBottomColor: theme.colors.borderMuted,
              }}>
                <SubHeading>Edit Booking</SubHeading>
                <Pressable onPress={() => {
                  setEditingBooking(null);
                  setEditSplit(null);
                  setEditSplitSeed(null);
                }}>
                  <Ionicons name="close" size={24} color={theme.colors.text} />
                </Pressable>
              </View>

              <ScrollView 
                style={{ flex: 1 }}
                contentContainerStyle={{
                  paddingHorizontal: theme.spacing(2),
                  paddingBottom: insets.bottom + theme.spacing(2),
                  paddingTop: theme.spacing(2),
                }}
                keyboardShouldPersistTaps="handled"
                keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'on-drag'}
                automaticallyAdjustKeyboardInsets={Platform.OS === 'ios'}
              >
                {/* Booking Info */}
                {editingBooking && (
                  <View style={{ marginBottom: theme.spacing(2) }}>
                    <ThemedText variant="bodyStrong" style={{ marginBottom: theme.spacing(0.5) }}>
                      {editingBooking.provider || editingBooking.location || 'Booking'}
                    </ThemedText>
                    <Caption color={theme.colors.textSecondary}>
                      {editingBooking.type} • {editingBooking.confirmationNumber || 'No confirmation #'}
                    </Caption>
                  </View>
                )}

                {/* Amount Input */}
                <View style={{ marginBottom: theme.spacing(2) }}>
                  <Caption color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(0.5) }}>
                    Amount
                  </Caption>
                  <TextInput
                    value={editAmount}
                    onChangeText={setEditAmount}
                    placeholder="Enter amount"
                    placeholderTextColor={theme.colors.textSecondary}
                    keyboardType="decimal-pad"
                    style={{
                      borderWidth: 1,
                      borderColor: theme.colors.border,
                      borderRadius: theme.radii.md,
                      padding: theme.spacing(1.5),
                      fontSize: 18,
                      fontWeight: '600',
                      color: theme.colors.text,
                      backgroundColor: theme.colors.surface,
                    }}
                  />
                </View>

                {/* Currency Select */}
                <View style={{ marginBottom: theme.spacing(2) }}>
                  <Caption color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(0.5) }}>
                    Currency
                  </Caption>
                  <SelectField
                    value={editCurrency}
                    onValueChange={setEditCurrency}
                    options={CURRENCY_OPTIONS}
                    placeholder="Select currency"
                  />
                  <Caption color={theme.colors.textSecondary} style={{ fontSize: 11, marginTop: theme.spacing(0.5) }}>
                    Select the currency used in the original booking confirmation
                  </Caption>
                </View>

                {/* Expense Split - only show for collaborative trips */}
                {tripParticipants.length > 1 && editAmount && parseFloat(editAmount) > 0 && (
                  <View style={{ marginBottom: theme.spacing(2) }}>
                    <ExpenseSplitSelector
                      key={`booking-split-${editingBooking?.id ?? 'none'}`}
                      splitContextId={editingBooking ? `booking-${editingBooking.id}` : 'booking-new'}
                      amount={parseFloat(editAmount) || 0}
                      currency={editCurrency || trip.baseCurrency || 'USD'}
                      tripMembers={tripParticipants}
                      currentUserId={userId}
                      initialSplit={editSplitSeed ?? undefined}
                      onSplitChange={setEditSplit}
                    />
                  </View>
                )}

                {/* Helpful note */}
                <View style={{
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  gap: theme.spacing(1),
                  padding: theme.spacing(1.5),
                  backgroundColor: theme.colors.primary + '10',
                  borderRadius: theme.radii.md,
                  marginBottom: theme.spacing(2),
                }}>
                  <Ionicons name="information-circle" size={18} color={theme.colors.primary} />
                  <Caption color={theme.colors.textSecondary} style={{ flex: 1, fontSize: 12 }}>
                    Updating the amount will recalculate your trip budget. Check the original email for the correct price.
                  </Caption>
                </View>

                {/* Save Button */}
                <Button
                  label={isSavingBooking ? 'Saving...' : 'Save Changes'}
                  onPress={handleSaveBookingEdit}
                  disabled={isSavingBooking || !editAmount || parseFloat(editAmount) <= 0}
                />
              </ScrollView>
            </View>
        </KeyboardAvoidingView>
      </Modal>
    </View>
  );
}

